import React, { useState } from 'react'
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import { useCurrentRole } from '../currentRole'
import { advancedColors } from '../theme'
import ComponentLibraryDialog from './ComponentLibraryDialog'
import { LayoutDefinition } from './layout'
import { PANEL_IDS, PANELS, PanelId, PanelOfferScope, isPanelOfferable } from './registry'

type Props = {
  uid: string
  layout: LayoutDefinition
  onAdd: (panel: PanelId) => void
  prominent: boolean
  scope: PanelOfferScope
}

const AddComponentTile = ({ uid, layout, onAdd, prominent, scope }: Props) => {
  const [role] = useCurrentRole()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const placed = new Set(layout.panels.map((entry) => entry.panel))
  const addable = PANEL_IDS.filter((id) => !placed.has(id) && isPanelOfferable(id, role, scope))

  const pick = (panel: PanelId) => {
    onAdd(panel)
    setMenuAnchor(null)
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Add a component to this page"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        sx={{
          border: `1px dashed ${advancedColors.lineStrong}`,
          borderRadius: 3,
          backgroundColor: advancedColors.surface,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          minHeight: prominent ? 320 : 96,
          cursor: 'pointer',
          color: advancedColors.inkMuted,
          '&:hover': { borderColor: advancedColors.green, color: advancedColors.green },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: prominent ? 40 : 28 }} />
        {prominent && <Typography variant="subtitle1">Add a component</Typography>}
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            setLibraryOpen(true)
          }}
        >
          <GridViewRoundedIcon fontSize="small" sx={{ mr: 1.5, color: advancedColors.inkMuted }} />
          Browse component library
        </MenuItem>
        <Divider />
        {addable.length === 0 && <MenuItem disabled>Every component is already on this page</MenuItem>}
        {addable.map((id) => (
          <MenuItem key={id} onClick={() => pick(id)}>
            {PANELS[id].title}
          </MenuItem>
        ))}
      </Menu>

      <ComponentLibraryDialog
        uid={uid}
        candidates={addable.map((id) => PANELS[id])}
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onAdd={onAdd}
      />
    </>
  )
}

export default AddComponentTile
